import { Injectable, TemplateRef } from '@angular/core';
import { Router } from '@angular/router';
import { BsModalService } from 'ngx-bootstrap/modal';
import { ToastNotificationService } from '../toast-notification/toast-notification.service';
import { Observable } from 'rxjs';
import { getErrorMessageFromHtmlResponse, isObject, isString } from '../../models/utils';
import { GenericTreeNode } from '../../components/generic-tree/generic-tree-node';
import { MainContentComponent } from '../../components/main-content/main-content.component';
import { MessageModalComponent, MessageModalData } from '../../components/modals/message-modal/message-modal.component';
import { BaseModalComponent, DataModalOptions, ModalResult } from '../../models/base-modal-component';
import { DocumentViewerContext } from '../../models/dr-document';
import { EntityType, HelpIndex, LocalStorageKey, MessageModalType, MessageType, SearchDialect, UpdateStatusType } from '../../models/enums';
import { ExternalAppData, ExternalDocumentViewerData, ExternalRRPromotionTracker, ExternalStatusManagementData, ExternalWorkbasketData } from '../../models/external-app-data';
import { Permissions } from '../../models/permission';
import { Project } from '../../models/project';
import { ServiceMessage } from '../../models/service-message';
import { User } from '../../models/user';
import { ViewType } from '../../models/view';
import { WorkingSet } from '../../models/working-set';
import { BroadcastChannel } from 'broadcast-channel';
import { enc, AES } from 'crypto-js';
import { DrFilter } from '../../models/dr-filter';
import { WorkBasketContext } from '../../models/task';
import { StatusManagementContext } from '../../models/job';
import { Comparison } from '../../models/comparison';
import { ReorderedDocumentListColumnInfo } from '../../components/document-list/document-list-info/document-list-column-info';
import { ContextMenuComponent } from '../../components/context-menu/context-menu.component';
import * as zip from "@zip.js/zip.js";

declare global {
    interface Window {
        navigateTo: (data: any) => void
    }
    interface Navigator {
        msSaveBlob(blob: any, defaultName?: string): boolean;
        msSaveOrOpenBlob(blob: any, defaultName?: string): boolean;
    }
}

export const MAX_TIMEOUT_LIMIT = 3;
@Injectable()
export class CommonService {

    get isLocalHost(): boolean { return window.location.hostname === 'localhost'; }
    private _debugServerIp = '172.31.240.149';
    public get debugServerIp(): string { return this._debugServerIp; }
    public set debugServerIp(value: string) {
        if (value && this._debugServerIp !== value) {
            this._debugServerIp = value;
            this.nonRestUrl = 'https://' + this.debugServerIp + ':8443';
            this.serverUrl = this.nonRestUrl + '/ediscovery/rest/';
            this.helpUrl = this.nonRestUrl + '/ediscovery/help/dr_ediscovery_CSH.htm#';
        }
    }
    public nonRestUrl = this.isLocalHost ? ('https://' + this.debugServerIp + ':8443') : window.location.origin;
    public serverUrl = this.nonRestUrl + '/ediscovery/rest/';
    private helpUrl = this.nonRestUrl + '/ediscovery/help/dr_ediscovery_CSH.htm#';
    public privacyPolicyUrl = 'https://www.transperfect.com/privacy/technology';

    private encryptionKey: string = 's6v9y$B&E)H@McQfTjWnZq4t7w!z%C*F-JaNdRgUkXp2s5u8x/A?D(G+KbPeShVm';
    private messages = {};
    private downloadConfirmationModal: any;
    private broadcastChannel = null;
    private broadcastChannelPerSessionName: string;

    private _currentBrowser: string;
    public get currentBrowser(): string {
        if (!this._currentBrowser) {
            if (/Firefox/i.test(navigator.userAgent)) {
                this._currentBrowser = 'Firefox';
            } else if (/MSIE/i.test(navigator.userAgent) || !!navigator.userAgent.match(/Trident\/7.0/)) {
                this._currentBrowser = 'InternetExplorer';
            } else if (/Chrome/i.test(navigator.userAgent)) {
                this._currentBrowser = 'Chrome';
            } else if (/Safari/i.test(navigator.userAgent)) {
                this._currentBrowser = 'Safari';
            }
        }
        return this._currentBrowser;
    }
    public get currentBrowserIsIE(): boolean {
        return this.currentBrowser === 'InternetExplorer';
    }

    private _isReview: boolean;
    public get isReview(): boolean {
        return this._isReview;
    }
    public set isReview(value: boolean) {
        this._isReview = value;
    }

    private _sessionToken: string;
    public get sessionToken(): string {
        return this._sessionToken;
    }
    public set sessionToken(value: string) {
        this._sessionToken = value;
    }

    private _isExternalApp: boolean;
    public get isExternalApp(): boolean {
        return this._isExternalApp;
    }
    public set isExternalApp(value: boolean) {
        this._isExternalApp = value;
    }

    private _currentUser: User;
    public get currentUser(): User {
        return this._currentUser;
    }
    public set currentUser(value: User) {
        if (value && !(value instanceof User)) {
            value = new User(value);
        }
        this._currentUser = value;
    }

    private _currentProject: Project;
    public get currentProject(): Project {
        return this._currentProject;
    }
    public set currentProject(value: Project) {
        if (value && !(value instanceof Project)) {
            value = new Project(value);
        }
        this._currentProject = value;
    }

    private _projectDataHandle: string;
    public get projectDataHandle(): string {
        return this._projectDataHandle;
    }
    public set projectDataHandle(value: string) {
        if (value) {
            this._projectDataHandle = value;
        }
    }

    private _contextHandle: string;
    public get contextHandle(): string {
        return this._contextHandle;
    }
    public set contextHandle(value: string) {
        this._contextHandle = value;
    }

    private _projectData: WorkingSet;
    public get projectData(): WorkingSet {
        return this._projectData;
    }
    public set projectData(value: WorkingSet) {
        if (value && !(value instanceof WorkingSet)) {
            value = new WorkingSet(value);
        }
        this._projectData = value;
    }

    private _discardPile: WorkingSet;
    public get discardPile(): WorkingSet {
        return this._discardPile;
    }
    public set discardPile(value: WorkingSet) {
        if (value && !(value instanceof WorkingSet)) {
            value = new WorkingSet(value);
        }
        this._discardPile = value;
    }

    private _hideArchivedItems: boolean;
    public get hideArchivedItems(): boolean {
        return this._hideArchivedItems;
    }
    public set hideArchivedItems(value: boolean) {
        this._hideArchivedItems = value;
    }

    private _lastAccessTimeForArchival: string;
    public get lastAccessTimeForArchival(): string {
        return this._lastAccessTimeForArchival;
    }
    public set lastAccessTimeForArchival(value: string) {
        this._lastAccessTimeForArchival = value;
    }

    private _moreButtonOriginalLabel: string;
    public get moreButtonOriginalLabel(): string {
        return this._moreButtonOriginalLabel;
    }
    public set moreButtonOriginalLabel(value: string) {
        this._moreButtonOriginalLabel = value;
    }

    private _projectName: string;
    public get projectName(): string {
        return this._projectName;
    }
    public set projectName(value: string) {
        this._projectName = value;
    }

    private _moreDataAreasButtonOriginalLabel: string;
    public get moreDataAreasButtonOriginalLabel(): string {
        return this._moreDataAreasButtonOriginalLabel;
    }
    public set moreDataAreasButtonOriginalLabel(value: string) {
        this._moreDataAreasButtonOriginalLabel = value;
    }

    private _selectedTreeNode: GenericTreeNode;
    public get selectedTreeNode(): GenericTreeNode {
        return this._selectedTreeNode;
    }
    public set selectedTreeNode(value: GenericTreeNode) {
        this._selectedTreeNode = value;
    }

    public get currentOrganizationHandle(): string {
        if (this.currentUser) {
            return this.currentUser.organizationHandle;
        }
    }

    private _isUserAdfsAuthenticated: boolean;
    public get isUserAdfsAuthenticated(): boolean {
        return this._isUserAdfsAuthenticated;
    }
    public set isUserAdfsAuthenticated(value: boolean) {
        this._isUserAdfsAuthenticated = value;
    }

    private _realmADFSEnabled: boolean;
    public get realmADFSEnabled(): boolean {
        return this._realmADFSEnabled;
    }
    public set realmADFSEnabled(value: boolean) {
        this._realmADFSEnabled = value;
    }

    private _searchDialect: SearchDialect;
    public get searchDialect(): SearchDialect {
        return this._searchDialect;
    }
    public set searchDialect(value: SearchDialect) {
        this._searchDialect = value;
    }

    private _mailServerConfig: string;
    public get mailServerConfig(): string {
        return this._mailServerConfig;
    }
    public set mailServerConfig(value: string) {
        this._mailServerConfig = value;
    }

    private _tokenizerConfigVersion: string;
    public get tokenizerConfigVersion(): string {
        return this._tokenizerConfigVersion;
    }
    public set tokenizerConfigVersion(value: string) {
        this._tokenizerConfigVersion = value;
    }

    private _dashboardReportsEnabled: boolean;
    public get dashboardReportsEnabled(): boolean {
        return this._dashboardReportsEnabled;
    }
    public set dashboardReportsEnabled(value: boolean) {
        this._dashboardReportsEnabled = value;
    }

    private _isGetPreferencesResponse: boolean;
    public get isGetPreferencesResponse(): boolean {
        return this._isGetPreferencesResponse;
    }
    public set isGetPreferencesResponse(value: boolean) {
        this._isGetPreferencesResponse = value;
    }

    private _useShortcutsApplication: boolean;
    public get useShortcutsApplication(): boolean {
        return this._useShortcutsApplication;
    }
    public set useShortcutsApplication(value: boolean) {
        this._useShortcutsApplication = value;
    }

    private _importsHandle: string;
    public get importsHandle(): string {
        return this._importsHandle;
    }
    public set importsHandle(value: string) {
        this._importsHandle = value;
    }

    private _suppressDuplicates: boolean;
    public get suppressDuplicates(): boolean {
        return this._suppressDuplicates;
    }
    public set suppressDuplicates(value: boolean) {
        this._suppressDuplicates = value;
    }

    private _archivalTimeForLeftTreeItems: number;
    public get archivalTimeForLeftTreeItems(): number {
        return this._archivalTimeForLeftTreeItems;
    }
    public set archivalTimeForLeftTreeItems(value: number) {
        this._archivalTimeForLeftTreeItems = value;
    }

    private _selectedNavigationNode: GenericTreeNode;
    public get selectedNavigationNode(): GenericTreeNode {
        return this._selectedNavigationNode;
    }
    public set selectedNavigationNode(value: GenericTreeNode) {
        this._selectedNavigationNode = value;
    }

    private _documentViewerOpenContext: DocumentViewerContext;
    public get documentViewerOpenContext(): DocumentViewerContext {
        return this._documentViewerOpenContext;
    }
    public set documentViewerOpenContext(value: DocumentViewerContext) {
        this._documentViewerOpenContext = value;
    }

    private _documentViewerCloseContext: DocumentViewerContext;
    public get documentViewerCloseContext(): DocumentViewerContext {
        return this._documentViewerCloseContext;
    }
    public set documentViewerCloseContext(value: DocumentViewerContext) {
        this._documentViewerCloseContext = value;
    }

    private _docListTabBeforeViewer: any;
    public get docListTabBeforeViewer(): any {
        return this._docListTabBeforeViewer;
    }
    public set docListTabBeforeViewer(value: any) {
        this._docListTabBeforeViewer = value;
    }

    private _workBasketContext: WorkBasketContext;
    public get workBasketContext(): WorkBasketContext {
        return this._workBasketContext;
    }
    public set workBasketContext(value: WorkBasketContext) {
        this._workBasketContext = value;
    }

    private _currentClusterView: GenericTreeNode;
    public get currentClusterView(): GenericTreeNode {
        return this._currentClusterView;
    }
    public set currentClusterView(value: GenericTreeNode) {
        this._currentClusterView = value;
    }

    private _expandedClusterView: any;
    public get expandedClusterView(): any {
        return this._expandedClusterView;
    }
    public set expandedClusterView(value: any) {
        this._expandedClusterView = value;
    }

    // task filters, visible columns
    private _workBasketInfoToRestore: Record<string, unknown>;
    public get workBasketInfoToRestore(): Record<string, unknown> {
        return this._workBasketInfoToRestore;
    }
    public set workBasketInfoToRestore(value: Record<string, unknown>) {
        this._workBasketInfoToRestore = value;
    }

    private _selectedComparisonNavigation: Comparison;
    public get selectedComparisonNavigation(): Comparison {
        return this._selectedComparisonNavigation;
    }
    public set selectedComparisonNavigation(value: Comparison) {
        this._selectedComparisonNavigation = value;
    }

    private _loadFileMetadataPrepareData: any;
    public get loadFileMetadataPrepareData(): any {
        return this._loadFileMetadataPrepareData;
    }
    public set loadFileMetadataPrepareData(value: any) {
        this._loadFileMetadataPrepareData = value;
    }

    private _mainContent: MainContentComponent;
    public get mainContent(): MainContentComponent {
        return this._mainContent;
    }
    public set mainContent(value: MainContentComponent) {
        this._mainContent = value;
    }

    private _userSelectionClick: boolean;
    public get userSelectionClick(): boolean {
        return this._userSelectionClick;
    }
    public set userSelectionClick(value: boolean) {
        this._userSelectionClick = value;
    }
    private _isFindDuplicates: boolean;
    public get isFindDuplicates(): boolean {
        return this._isFindDuplicates;
    }
    public set isFindDuplicates(value: boolean) {
        this._isFindDuplicates = value;
    }

    private _selectedErrorNavigationNode: GenericTreeNode;
    public get selectedErrorNavigationNode(): GenericTreeNode {
        return this._selectedErrorNavigationNode;
    }
    public set selectedErrorNavigationNode(value: GenericTreeNode) {
        this._selectedErrorNavigationNode = value;
    }

    private _isTagTaskInProgress: boolean;
    public get isTagTaskInProgress(): boolean {
        return this._isTagTaskInProgress;
    }
    public set isTagTaskInProgress(value: boolean) {
        this._isTagTaskInProgress = value;
    }

    private _noOfRequestsWithHttpResponseError: number;
    public get noOfRequestsWithHttpResponseError(): number {
        return this._noOfRequestsWithHttpResponseError;
    }
    public set noOfRequestsWithHttpResponseError(value: number) {
        this._noOfRequestsWithHttpResponseError = value;
    }

    private _currentWorkbasketFilters: Array<DrFilter>;
    public get currentWorkbasketFilters(): Array<DrFilter> {
        return this._currentWorkbasketFilters;
    }
    public set currentWorkbasketFilters(value: Array<DrFilter>) {
        this._currentWorkbasketFilters = value;
    }

    private _docViewerOpened: boolean;
    public get docViewerOpened(): boolean {
        return this._docViewerOpened;
    }
    public set docViewerOpened(value: boolean) {
        this._docViewerOpened = value;
    }

    private _pendingDeletionTags: Array<GenericTreeNode>;
    public get pendingDeletionTags(): Array<GenericTreeNode> {
        return this._pendingDeletionTags;
    }
    public set pendingDeletionTags(value: Array<GenericTreeNode>) {
        this._pendingDeletionTags = value;
    }

    private _tagNode: GenericTreeNode;
    public get tagNode(): GenericTreeNode {
        return this._tagNode;
    }
    public set tagNode(value: GenericTreeNode) {
        this._tagNode = value;
    }

    private _statusManagementContext: StatusManagementContext;
    public get statusManagementContext(): StatusManagementContext {
        return this._statusManagementContext;
    }
    public set statusManagementContext(value: StatusManagementContext) {
        this._statusManagementContext = value;
    }

    private _suppressGridSpinnerIfPoller: boolean;
    public get suppressGridSpinnerIfPoller(): boolean {
        return this._suppressGridSpinnerIfPoller;
    }
    public set suppressGridSpinnerIfPoller(value: boolean) {
        this._suppressGridSpinnerIfPoller = value;
    }

    private _suppressGridSpinner: boolean;
    public get suppressGridSpinner(): boolean {
        return this._suppressGridSpinner;
    }
    public set suppressGridSpinner(value: boolean) {
        this._suppressGridSpinner = value;
    }

    private _comboboxDropdownHidden: boolean;
    public get comboboxDropdownHidden(): boolean {
        return this._comboboxDropdownHidden;
    }
    public set comboboxDropdownHidden(value: boolean) {
        this._comboboxDropdownHidden = value;
    }
    private _externalParsingLibrary: string;
    public get externalParsingLibrary(): string {
        return this._externalParsingLibrary;
    }
    public set externalParsingLibrary(value: string) {
        this._externalParsingLibrary = value;
    }

    private _tlsExpressSystem: boolean;
    public get tlsExpressSystem(): boolean {
        return this._tlsExpressSystem;
    }
    public set tlsExpressSystem(value: boolean) {
        this._tlsExpressSystem = value;
    }

    private _isTPAuthEnabledSystem: boolean;
    public get isTPAuthEnabledSystem(): boolean {
        return this._isTPAuthEnabledSystem;
    }
    public set isTPAuthEnabledSystem(value: boolean) {
        this._isTPAuthEnabledSystem = value;
    }

    private _maxBooleanClauseCount: number;
    public get maxBooleanClauseCount(): number {
        return this._maxBooleanClauseCount;
    }
    public set maxBooleanClauseCount(value: number) {
        this._maxBooleanClauseCount = value;
    }

    private _appContext: any;
    public get appContext(): any {
        return this._appContext;
    }
    public set appContext(value: any) {
        this._appContext = value;
    }

    private _isCalculatingStorage: boolean;
    public get isCalculatingStorage(): boolean {
        return this._isCalculatingStorage;
    }
    public set isCalculatingStorage(value: boolean) {
        this._isCalculatingStorage = value;
    }

    private _matrixGridsSelection: any;
    public get matrixGridsSelection(): any {
        return this._matrixGridsSelection;
    }
    public set matrixGridsSelection(value: any) {
        this._matrixGridsSelection = value;
    }

    private _keepGridSpinner: boolean;
    public get keepGridSpinner(): boolean {
        return this._keepGridSpinner;
    }
    public set keepGridSpinner(value: boolean) {
        this._keepGridSpinner = value;
    }

    private _policyAcknowledgment: boolean;
    public get policyAcknowledgment(): boolean {
        return this._policyAcknowledgment;
    }
    public set policyAcknowledgment(value: boolean) {
        this._policyAcknowledgment = value;
    }

    private _reefReviewEnabled: boolean;
    public get reefReviewEnabled(): boolean {
        return this._reefReviewEnabled;
    }
    public set reefReviewEnabled(value: boolean) {
        this._reefReviewEnabled = value;
    }

    private _termSearchHitCountReportsDownloaded: Array<any> = [];
    public get termSearchHitCountReportsDownloaded(): Array<any> {
        return this._termSearchHitCountReportsDownloaded;
    }
    public set termSearchHitCountReportsDownloaded(value: Array<any>) {
        this._termSearchHitCountReportsDownloaded = value;
    }

    private _preferences: any;
    public get preferences(): any {
        return this._preferences;
    }
    public set preferences(value: any) {
        this._preferences = value;
    }

    isDRExportEnabled(): boolean {
        return !!this.modalService?.config?.initialState?.isDRExportEnabled;
    }

    setMockMode(): void {
        this._debugServerIp = 'localhost';
        this.nonRestUrl = 'http://localhost:8443';
        this.serverUrl = this.nonRestUrl + '/ediscovery/rest/';
        this.helpUrl = this.nonRestUrl + '/ediscovery/help/dr_ediscovery_CSH.htm#';
    }

    constructor(private modalService: BsModalService, private contextMenuComponent: ContextMenuComponent, private router: Router, private toastNotificationService: ToastNotificationService) {
        this.messages = {};
        this.noOfRequestsWithHttpResponseError = 0;
        this.getEnumNames(MessageType).forEach(element => {
            this.messages[element] = new ServiceMessage();
        });
        this.checkIfExternalDocumentViewer();
        this.checkIfExternalWorkbasket();
        this.checkIfExternalStatusManagement();
        this.checkIfExternalRRPromotionTracker();
        if (!this.isExternalApp) {
            const uniquePerSessionChannelName = 'DR' + performance.now();
            this.initializeBroadcastChannel(uniquePerSessionChannelName);
        }

        const errorMessageFromForcedReload = localStorage.getItem(LocalStorageKey.AppForcedReload);
        if (errorMessageFromForcedReload) {
            localStorage.removeItem(LocalStorageKey.AppForcedReload);
            setTimeout(() => {
                if (errorMessageFromForcedReload === 'loggedOutFromAnotherDeviceOrWindow') {
                    this.showLogoutOnAllDevicesInfoMessage();
                } else {
                    this.openMessageModal(MessageModalType.error, errorMessageFromForcedReload);
                }
            }, 0);
        }
    }

    private initializeBroadcastChannel(channelName: string): void {
        if (channelName) {
            this.broadcastChannelPerSessionName = channelName;
            this.broadcastChannel = new BroadcastChannel(this.broadcastChannelPerSessionName);
        }
    }

    private setupBroadcastChannelForExternalApp(extAppData: ExternalAppData): void {
        if (extAppData) {
            this.initializeBroadcastChannel(extAppData.broadcastChannelName);
            this.broadcastChannel.onmessage = (event) => {
                this.handleCrossBrowserEvent(event);
            };
        }
    }

    private checkIfExternalDocumentViewer(): void {
        const externalAppData = this.getCacheData(ExternalDocumentViewerData.cacheName) as ExternalDocumentViewerData;
        if (externalAppData) {
            this.setupBroadcastChannelForExternalApp(externalAppData);
            this.setCacheData(ExternalDocumentViewerData.cacheName, null);
            if (externalAppData.sessionToken && externalAppData.currentProject && externalAppData.documentViewerContext) {
                this.sessionToken = externalAppData.sessionToken;
                this.currentProject = externalAppData.currentProject;
                this.isTPAuthEnabledSystem = externalAppData.isTPAuthEnabledSystem;
                Permissions.setPermissions(externalAppData.userPermissions);
                this.projectData = externalAppData.projectData;
                if (externalAppData.debugServerIp) {
                    this.debugServerIp = externalAppData.debugServerIp;
                }
                this.documentViewerOpenContext = new DocumentViewerContext(externalAppData.documentViewerContext);
                this.selectedNavigationNode = new GenericTreeNode(externalAppData.selectedNavigationNode);
                if (externalAppData.currentClusterView) {
                    this.currentClusterView = new GenericTreeNode(externalAppData.currentClusterView);
                }
                this._isExternalApp = true;
                this.router.navigate([ExternalDocumentViewerData.appName], { skipLocationChange: true });
                if (window.document) {
                    window.document.title = 'eDiscovery Doc Viewer';
                }
            }
        }
    }

    private checkIfExternalWorkbasket(): void {
        const externalAppData = this.getCacheData(ExternalWorkbasketData.cacheName) as ExternalWorkbasketData;
        if (externalAppData) {
            this.setupBroadcastChannelForExternalApp(externalAppData);
            this.setCacheData(ExternalWorkbasketData.cacheName, null);
            if (externalAppData.sessionToken && externalAppData.currentProject) {
                this.sessionToken = externalAppData.sessionToken;
                this.currentProject = externalAppData.currentProject;
                this.projectDataHandle = externalAppData.projectDataHandle;
                this.isTPAuthEnabledSystem = externalAppData.isTPAuthEnabledSystem;
                Permissions.setPermissions(externalAppData.userPermissions);
                if (externalAppData.debugServerIp) {
                    this.debugServerIp = externalAppData.debugServerIp;
                }
                this.workBasketContext = new WorkBasketContext(externalAppData.workBasketContext);
                this._isExternalApp = true;
                this.router.navigate([ExternalWorkbasketData.appName], { skipLocationChange: true });
                if (window.document) {
                    window.document.title = 'eDiscovery Work Basket';
                }
            }
        }
    }

    private checkIfExternalStatusManagement(): void {
        const externalAppData = this.getCacheData(ExternalStatusManagementData.cacheName) as ExternalStatusManagementData;
        if (externalAppData) {
            this.setupBroadcastChannelForExternalApp(externalAppData);
            this.setCacheData(ExternalStatusManagementData.cacheName, null);
            if (externalAppData.sessionToken) {
                this.sessionToken = externalAppData.sessionToken;
                this.currentProject = externalAppData.currentProject;
                this.currentUser = externalAppData.currentUser;
                this.projectDataHandle = externalAppData.projectDataHandle;
                this.appContext = externalAppData.appContext;
                this.isTPAuthEnabledSystem = externalAppData.isTPAuthEnabledSystem;
                Permissions.setPermissions(externalAppData.userPermissions);
                if (externalAppData.debugServerIp) {
                    this.debugServerIp = externalAppData.debugServerIp;
                }
                this.statusManagementContext = new StatusManagementContext(externalAppData.statusManagementContext);
                this._isExternalApp = true;
                this.router.navigate([ExternalStatusManagementData.appName], { skipLocationChange: true });
                if (window.document) {
                    window.document.title = 'eDiscovery Status';
                }
            }
        }
    }

    private checkIfExternalRRPromotionTracker() {
        const externalAppData = this.getCacheData(ExternalRRPromotionTracker.cacheName) as ExternalRRPromotionTracker;
        if (externalAppData) {
            this.setupBroadcastChannelForExternalApp(externalAppData);
            this.setCacheData(ExternalRRPromotionTracker.cacheName, null);

            if (externalAppData.sessionToken) {
                this.sessionToken = externalAppData.sessionToken;
                this.currentProject = externalAppData.currentProject;
                this.projectDataHandle = externalAppData.projectDataHandle;
                this.isTPAuthEnabledSystem = externalAppData.isTPAuthEnabledSystem;
                Permissions.setPermissions(externalAppData.userPermissions);
                if (externalAppData.debugServerIp) {
                    this.debugServerIp = externalAppData.debugServerIp;
                }
                this._isExternalApp = true;
                this.router.navigate([ExternalRRPromotionTracker.appName], { skipLocationChange: true });
                if (window.document) {
                    window.document.title = 'Promotion Tracking';
                }
            }
        }
    }

    // This is called by child windows to handle events they picked up from the parent window/tab.
    private handleCrossBrowserEvent(event: any): void {
        if (event === MessageType.logOut) {
            this.sendMessage(MessageType.stopPollingOnActiveUserSessions);
            this.openHardErrorModal('Your login session has expired or you logged out.', 'Close Window', () => {
                window.close();
            });
            this.clearData();
        }
    }

    openExternalDocumentViewer(documentViewerContext: DocumentViewerContext): void {
        const data = new ExternalDocumentViewerData({
            sessionToken: this.sessionToken,
            broadcastChannelName: this.broadcastChannelPerSessionName,
            currentProject: this.currentProject,
            userPermissions: Permissions.mapToObjectActionList(Permissions.userPermissions),
            projectData: this.projectData,
            debugServerIp: this.serverUrl.includes(this.debugServerIp) ? this.debugServerIp : undefined,
            documentViewerContext: documentViewerContext,
            isTPAuthEnabledSystem: this.isTPAuthEnabledSystem,
            selectedNavigationNode: new GenericTreeNode({
                name: this.selectedNavigationNode.name,
                entityType: this.selectedNavigationNode.entityType,
                data: this.selectedNavigationNode.data,
                navigationInfo: this.selectedNavigationNode.navigationInfo
            }),
            currentClusterView: this.currentClusterView ? new GenericTreeNode({
                name: this.currentClusterView.name,
                entityType: this.currentClusterView.entityType,
                data: this.currentClusterView.data
            }) : undefined
        });

        if (this.documentViewerOpenContext?.isGeneratedComGridsDocList) {
            this.matrixGridsSelection.isDocViewerOpened = true;
            data.documentViewerContext.isGeneratedComGridsDocList = true;
            data.documentViewerContext.selectedDocument.handle = this.documentViewerOpenContext?.selectedDocument?.handle;
        }
        if (this.documentViewerOpenContext?.isShortMessageThreads) {
            data.documentViewerContext.isShortMessageThreads = this.documentViewerOpenContext?.isShortMessageThreads;
        }
        this.setCacheData(ExternalDocumentViewerData.cacheName, data);
        window.open(window.location.href, 'width=1440,height=768,left=100,top=100,resizable=1');
    }

    openExternalWorkbasket(workBasketContext: WorkBasketContext): void {
        const data = new ExternalWorkbasketData({
            sessionToken: this.sessionToken,
            broadcastChannelName: this.broadcastChannelPerSessionName,
            currentProject: this.currentProject,
            userPermissions: Permissions.mapToObjectActionList(Permissions.userPermissions),
            debugServerIp: this.serverUrl.includes(this.debugServerIp) ? this.debugServerIp : undefined,
            projectDataHandle: this.projectData ? this.projectData.handle : null,
            isTPAuthEnabledSystem: this.isTPAuthEnabledSystem,
            workBasketContext: workBasketContext
        });
        this.setCacheData(ExternalWorkbasketData.cacheName, data);
        window.open(window.location.href, 'width=80%,left=100,top=100,resizable=1');
    }

    openExternalRRPromotionTracker(): void {
        const data = new ExternalRRPromotionTracker({
            sessionToken: this.sessionToken,
            broadcastChannelName: this.broadcastChannelPerSessionName,
            currentProject: this.currentProject,
            userPermissions: Permissions.mapToObjectActionList(Permissions.userPermissions),
            debugServerIp: this.serverUrl.includes(this.debugServerIp) ? this.debugServerIp : undefined,
            projectDataHandle: this.projectData ? this.projectData.handle : null,
            isTPAuthEnabledSystem: this.isTPAuthEnabledSystem,
        });
        this.setCacheData(ExternalRRPromotionTracker.cacheName, data);
        window.open(window.location.href, 'width=80%,left=100,top=100,resizable=1');
    }

    openExternalStatusManagement(statusManagementContext: StatusManagementContext): void {
        const data = new ExternalStatusManagementData({
            sessionToken: this.sessionToken,
            broadcastChannelName: this.broadcastChannelPerSessionName,
            currentProject: this.currentProject,
            appContext: this.appContext,
            currentUser: this.currentUser,
            userPermissions: Permissions.mapToObjectActionList(Permissions.userPermissions),
            debugServerIp: this.serverUrl.includes(this.debugServerIp) ? this.debugServerIp : undefined,
            statusManagementContext: statusManagementContext,
            isTPAuthEnabledSystem: this.isTPAuthEnabledSystem,
        });
        this.setCacheData(ExternalStatusManagementData.cacheName, data);
        const popup = window.open(window.location.href, 'width=80%,left=50,top=50,resizable=1');
        if (popup !== null) {
            popup.moveTo(15, 20);
            popup.resizeTo(screen.width - 30, screen.height - 75);
        }
    }

    // cache
    setCacheData(key: string, value: any): void {
        if (typeof key === 'string' && typeof (Storage) !== 'undefined') {
            if (value) {
                localStorage.setItem(key, this.encrypt(JSON.stringify(value)));
            } else {
                localStorage.removeItem(key);
            }
        }
    }

    getCacheData(key: string): any {
        if (typeof key === 'string' && typeof (Storage) !== 'undefined') {
            const value = localStorage.getItem(key);
            try {
                if (value) {
                    return JSON.parse(this.decrypt(value));
                }
            } catch (e) {
                console.log(e);
                localStorage.removeItem(value);
            }
        }
    }

    private encrypt(itemToEncrypt: string): string {
        return AES.encrypt(itemToEncrypt, this.encryptionKey).toString();
    }

    private decrypt(itemToDecrypt: string): string {
        return AES.decrypt(itemToDecrypt, this.encryptionKey).toString(enc.Utf8);
    }

    getUuid(loggedUser): string {
        const cacheData = this.getCacheData(LocalStorageKey.Uuid);
        if (cacheData) {
            const userAccessList = cacheData.split('=');
            let uid = null;
            if (loggedUser) {
                for (let i = 0; i < userAccessList.length; i++) {
                    const client = userAccessList[i].split('||');
                    const user = client[0];
                    const clientUuid = client[1];

                    if (user === loggedUser.trim()) {
                        uid = clientUuid;
                        break;
                    }
                }
            }
            return uid;
        }
    }

    updateAppContextAndStoreIt(projectHandle: string): void {
        if (this.appContext) {
            if (projectHandle) {
                this.appContext.prjHandle = projectHandle;
            }
            this.appContext.isTPAuthEnabledSystem = this.isTPAuthEnabledSystem;
            if (this.isTPAuthEnabledSystem) {
                this.appContext.sessionToken = this.sessionToken;
            }
            this.setCacheData(LocalStorageKey.AppContext, this.appContext);
        }
    }

    clearData(onlyProjectLevelData = false): void {
        if (!onlyProjectLevelData) {
            this.sessionToken = 'bogus:bogus';
            this.currentUser = null;
            this.isUserAdfsAuthenticated = false;
            this.realmADFSEnabled = false;
        }

        Permissions.clearPermissions();
        this.currentProject = null;
        if (this.currentUser) {
            // customerName != originalCustomerName is true for system users
            this.contextHandle = this.currentUser.customerName !== this.currentUser.originalCustomerName ? this.currentUser.originalCustomerName : this.currentUser.customerName;
        } else {
            this.contextHandle = null;
        }
        this.projectData = null;
        this.discardPile = null;
        this.selectedNavigationNode = null;
        this.documentViewerOpenContext = null;
        this.documentViewerCloseContext = null;
        this.statusManagementContext = null;
        this.workBasketContext = null;
        this.searchDialect = null;
        this.mailServerConfig = null;
        this.tokenizerConfigVersion = null;
        this.dashboardReportsEnabled = false;
        this.useShortcutsApplication = false;
        this.importsHandle = null;
        this.suppressDuplicates = false;
        this.hideArchivedItems = false;
        this.archivalTimeForLeftTreeItems = null;
        this.isExternalApp = false;
        this.workBasketInfoToRestore = null;
        this.currentWorkbasketFilters = null;
        this.keepGridSpinner = false;
        // close all open context menus
        this.contextMenuComponent.closeAllContextMenus();
        // clear dirty column state on doc list grids
        ReorderedDocumentListColumnInfo.clear();
    }

    // messaging
    sendMessage(messageType: MessageType, data?: any): void {
        this.messages[messageType].messageSource.next(data);
    }

    getMessage(messageType: MessageType): Observable<any> {
        return this.messages[messageType].message;
    }

    // toasts notifications
    openSuccessToastNotification(message: string): void {
        this.toastNotificationService.success(message);
    }

    openInformationToastNotification(message: string): void {
        this.toastNotificationService.information(message);
    }

    openErrorToastNotification(message: string): void {
        this.toastNotificationService.error(message);
    }

    openWarningToastNotification(message: string): void {
        this.toastNotificationService.warning(message);
    }

    // modals
    openHelp(helpIndex: HelpIndex): void {
        if (helpIndex) {
            window.open(this.helpUrl + helpIndex, '_blank');
        }
    }

    openConfirmationWarningModal(message: string, title?: string, okBtnText = 'Yes', cancelBtnText = 'No', secondBtnText?: string, customClass?: string): Promise<ModalResult> {
        if (message) {
            return this.openMessageModal(MessageModalType.warning, message, title, okBtnText, cancelBtnText, secondBtnText, null, null, null, customClass);
        }
    }
    openConfirmationDeleteModal(message: string, title?: string, okBtnText = 'Yes', cancelBtnText = 'No', secondBtnText?: string, isDeleteProject?: boolean): Promise<ModalResult> {
        if (message) {
            return this.openMessageModal(MessageModalType.confirmationDelete, message, title, okBtnText, cancelBtnText, secondBtnText, null, null, null, null, isDeleteProject);
        }
    }

    openConfirmationInfoModal(message: string, title?: string, okBtnText = 'Yes', cancelBtnText = 'No', secondBtnText?: string): Promise<ModalResult> {
        if (message) {
            return this.openMessageModal(MessageModalType.confirmationInfo, message, title, okBtnText, cancelBtnText, secondBtnText);
        }
    }

    openConfirmationModal(message: string, title?: string, okBtnText = 'Yes', cancelBtnText = 'No', // decided to remove the type of the parameters as part of clearing eslint error as it is already inferred when a property is assigned 
        secondBtnText?: string, containerAutomationId?: string, disableSaveButton?: boolean): Promise<ModalResult> {
        if (message) {
            return this.openMessageModal(MessageModalType.confirmation, message, title, okBtnText, cancelBtnText, secondBtnText, containerAutomationId, null, disableSaveButton);
        }
    }

    openDiscardConfirmationModal(message: string, title?: string, okBtnText = 'Yes', cancelBtnText = 'No',
        containerAutomationId?: string): Promise<ModalResult> {
        if (message) {
            return this.openDiscardMessageModal(MessageModalType.confirmation, message, title, okBtnText, cancelBtnText, containerAutomationId);
        }
    }

    openWarningModal(message: string, title?: string): Promise<ModalResult> {
        if (message) {
            return this.openMessageModal(MessageModalType.warning, message, title);
        }
    }

    openWarningWithCounterModal(message: string, title?: string, okBtnText?: string, counterStartValue?: number): Promise<ModalResult> {
        if (message) {
            const modalData: MessageModalData = {
                type: MessageModalType.warning, message: message, title: title,
                okBtnText: null, cancelBtnText: null, secondBtnText: okBtnText,
                containerAutomationId: 'session-expired',
                closeButtonVisible: false,
                durationCounter: counterStartValue
            };
            return this.openModal(MessageModalComponent, new DataModalOptions({ modalData: modalData, keyboard: false }));
        }
    }

    openErrorModal(error: any, title?: string, checkViewNotFoundMsg = true): Promise<ModalResult> {
        if (!this.currentUser && this.modalService.getModalsCount() > 0) {
            // user already logged out and the error is still shown
            return;
        }
        if (error) {
            let errorMessage = this.getErrorMessage(error);
            const userWasLoggedOutFromAnotherDevice = errorMessage.includes('User is not logged in');
            if (userWasLoggedOutFromAnotherDevice) {
                if (!this.isExternalApp) {
                    this.clearRememberMeDataFromLocalStorage();
                    this.reloadAppAndShowLoginWithMessage();
                }
                return null;
            }

            if (errorMessage.includes('View not found') && checkViewNotFoundMsg) {
                return null;
            }

            const isSessionExpiredError = errorMessage.includes('The current session expired');
            const serviceNodeIsDown = errorMessage.includes('No more destinations are available');
            if (this.isExternalApp && (isSessionExpiredError || serviceNodeIsDown)) {
                return null;
            }
            if ((errorMessage.includes('Server is offline') && this.noOfRequestsWithHttpResponseError === MAX_TIMEOUT_LIMIT)
                || isSessionExpiredError || serviceNodeIsDown) {
                if (serviceNodeIsDown) {
                    errorMessage = 'Server is offline! Try again later.';
                }
                const openModalsCount = this.modalService.getModalsCount();
                if (openModalsCount > 0) {
                    this.reloadAppAndShowLoginWithMessage(errorMessage);
                    return null;
                } else {
                    this.sendMessage(MessageType.sessionExpired);
                }
            }
            if (!serviceNodeIsDown && (errorMessage.includes('Server is offline') && this.noOfRequestsWithHttpResponseError >= 0 && this.noOfRequestsWithHttpResponseError < MAX_TIMEOUT_LIMIT)) {
                if (this.noOfRequestsWithHttpResponseError === 1) {
                    return this.openMessageModal(MessageModalType.error, 'Server failed to respond.', title);
                }
                return null;
            }
            if (errorMessage.includes('Timeout has occurred')) {
                errorMessage = 'Server failed to respond. Please try again.';
            }
            if (errorMessage.includes('VECTOR_SET_TOO_SMALL_TO_CLUSTER_WITH_REQUESTED_CLUSTER_SIZE')) {
                errorMessage = 'The document set is too small to cluster.';
            }
            if (errorMessage.includes('Cause:Invalid') || errorMessage.includes('Cannot Parse search:')) {
                if (this.modalService.getModalsCount() === 0) {
                    return this.openMessageModal(MessageModalType.error, errorMessage, title);
                }
            } else {
                return this.openMessageModal(MessageModalType.error, errorMessage, title);
            }
        } else {
            console.error(`Attempted to display error, but it's empty.`);
        }
    }

    showLogoutOnAllDevicesInfoMessage(): void {
        this.openInformationToastNotification('You have been logged out on all devices. \n You will need to re-enter your password to log in\n even if you had the "Remember Me" feature enabled.'.replace(/\n/g, '<br/>'));
    }

    reloadAppAndShowLoginWithMessage(messageToShow = null): void {
        if (!this.isExternalApp) {
            const defaultLoggedOutMessage = 'loggedOutFromAnotherDeviceOrWindow';
            localStorage.setItem(LocalStorageKey.AppForcedReload, messageToShow ? messageToShow : defaultLoggedOutMessage);
            // notify child windows to close due to logout
            this.broadcastChannel.postMessage(MessageType.logOut);
            location.reload();
        }
    }

    navigateToLoginAndNotifyChildWindows(): void {
        if (!this.isExternalApp) {
            this.broadcastChannel.postMessage(MessageType.logOut);
            this.router.navigate(['']);
        }
    }

    clearRememberMeDataFromLocalStorage(): void {
        this.setCacheData(LocalStorageKey.RememberMeWithToken, null);
    }

    openCustomContentMessageModal(contentTemplate: TemplateRef<any>, type: MessageModalType, title: string, okBtnText: string, cancelBtnText: string,
        secondBtnText: string, containerAutomationId?: string): Promise<ModalResult> {
        if (contentTemplate) {
            return this.openMessageModal(type, null, title, okBtnText, cancelBtnText, secondBtnText, containerAutomationId, contentTemplate);
        }
    }

    openHardErrorModal(message: string, buttonText: string, action: () => void): void {
        const modalData: MessageModalData = {
            type: MessageModalType.error, message: message, title: 'Error',
            cancelBtnText: buttonText,
            cancelBtnFunc: action
        };
        this.openModal(MessageModalComponent, new DataModalOptions({ modalData: modalData }));
    }

    private openMessageModal(type: MessageModalType, message: string, title?: string, okBtnText?: string,
        cancelBtnText?: string, secondBtnText?: string, containerAutomationId?: string, contentTemplate?: TemplateRef<any>,
        disableSaveButton?: boolean, customClass?: string, isDeleteProject?: boolean): Promise<ModalResult> {
        if (type && (message || contentTemplate)) {
            const modalData: MessageModalData = {
                type: type, message: message, title: title,
                okBtnText: okBtnText, cancelBtnText: cancelBtnText, secondBtnText: secondBtnText,
                containerAutomationId: containerAutomationId,
                contentTemplate: contentTemplate,
                disableSaveButton: disableSaveButton,
                customClass: customClass,
                isDeleteProject: isDeleteProject
            };
            return this.openModal(MessageModalComponent, new DataModalOptions({ modalData: modalData }));
        }
    }

    private openDiscardMessageModal(type: MessageModalType, message: string, title?: string, okBtnText?: string,
        cancelBtnText?: string, containerAutomationId?: string, contentTemplate?: TemplateRef<any>): Promise<ModalResult> {
        if (type && (message || contentTemplate)) {
            const modalData: MessageModalData = {
                type: type, message: message, title: title,
                okBtnText: okBtnText, cancelBtnText: cancelBtnText,
                containerAutomationId: containerAutomationId,
                contentTemplate: contentTemplate
            };
            return this.openModal(MessageModalComponent, new DataModalOptions({ modalData: modalData }));
        }
    }

    openModal(modalComponent: any, data?: Partial<DataModalOptions>): Promise<ModalResult> {
        if (modalComponent) {
            data = new DataModalOptions(data);
            data.initialState = {
                ...(data.initialState || {}),
                isDRExportEnabled: data.isDRExport ?? false
            };

            let baseModal = this.modalService.show(modalComponent, data).content;

            if (baseModal && baseModal.message && (baseModal.message as string).indexOf('Report download is being generated.') === 0) {
                this.downloadConfirmationModal = baseModal;
            }

            // additional check for modals that do not inherit BaseModalComponent
            // but use another common component that itself inherits BaseModalComponent (NewDataSetModalComponent, AddLoadFileModalComponent)
            if (baseModal.modalComponent instanceof BaseModalComponent) {
                baseModal = modalComponent as Record<string, unknown>;
            }
            if (baseModal instanceof BaseModalComponent) {
                return baseModal.onClose.promise;
            }
        }
        return Promise.resolve({ modalResult: false, modalData: null });
    }

    getErrorMessage(error: any): string {
        let errorMessage = '';
        if (isString(error)) {
            errorMessage = error;
        } else if (isObject(error)) {
            if (isString(error.error)) {
                errorMessage = error.error as string;
                if (errorMessage.includes('auraria-service-tier not bound') || errorMessage.includes('Could not obtain connection to any of these URLs')) {
                    errorMessage = 'Server is temporarily unavailable.';
                } else if (errorMessage.includes('DrWsException')) {
                    const info = errorMessage.split('DrWsException: ');
                    const formattedMessage = info.length > 1 ? 'The following operation failed: ' +
                        info[1].split(':')[0] + '\n\n' +
                        'System processing error: ' +
                        info[1].split(':')[1] + '\n\n' +
                        'Details: ' +
                        info[1].split('\n')[0] : 'The following operation failed: ' +
                    errorMessage;
                    errorMessage = formattedMessage;
                } else if (errorMessage.includes('HTTP Status 404')) {
                    errorMessage = 'The requested resource is not available';
                } else if (errorMessage.includes('<html>')) {
                    errorMessage = getErrorMessageFromHtmlResponse(errorMessage);
                }
            } else if (error.status === 0) {
                errorMessage = 'Server is offline! Try again later.';
            } else if (isString(error.message)) {
                errorMessage = error.message as string;
            } else if (isString(error.extendedStatus)) {
                errorMessage = error.extendedStatus as string;
            }
        }
        if (errorMessage.includes('Unauthorized') || errorMessage.includes('Invalid User')) {
            errorMessage = 'The current session expired! Please log in again.';
        }
        return errorMessage || 'Unknown Error';
    }

    // utility
    convertArrayToBinaryString(data): string {
        let binary = '';
        const bytes = new Uint8Array(data);
        const length = bytes.byteLength;

        for (let i = 0; i < length; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return binary;
    }

    formatSizeUnits(bytes: any): string { // leaving bytes as any as the number gets converted to a string when 'return'
        if (!isNaN(bytes)) {
            if (bytes >= 1099511627776) {
                bytes = (bytes / 1099511627776).toFixed(1) + ' TB';
            } else if (bytes >= 1073741824) {
                bytes = (bytes / 1073741824).toFixed(1) + ' GB';
            } else if (bytes >= 1048576) {
                bytes = (bytes / 1048576).toFixed(1) + ' MB';
            } else if (bytes >= 1024) {
                bytes = (bytes / 1024).toFixed(1) + ' KB';
            } else if (bytes > 1) {
                bytes = bytes + ' B';
            } else if (bytes === 1) {
                bytes = bytes + ' B';
            } else if (bytes < 1) {
                bytes = bytes + ' B';
            } else {
                bytes = '0 B';
            }
        }
        return bytes;
    }

    getEnumNames(e: any): Array<string> {
        return this.getObjValues(e).filter(v => typeof v === 'string') as string[];
    }

    getEnumValues<T extends number>(e: any): Array<T> {
        return this.getObjValues(e).filter(v => typeof v === 'number') as T[];
    }

    private getObjValues(e: any): (number | string)[] {
        return Object.keys(e).map(k => e[k]);
    }

    entityTypeToUpdateStatusType(entityType: EntityType): UpdateStatusType {
        switch (entityType) {
            case EntityType.import: return UpdateStatusType.CORPUS;
            case EntityType.export: return UpdateStatusType.EXPORT_VIEW;
            case EntityType.exportVolume: return UpdateStatusType.EXPORT_VIEW;
            case EntityType.projectData: return UpdateStatusType.WORKING_SET;
            case EntityType.custodian: return UpdateStatusType.CUSTODIAN;
            case EntityType.mediaID: return UpdateStatusType.MEDIAID;
            case EntityType.batch: return UpdateStatusType.BATCH;
            case EntityType.tagView: return UpdateStatusType.TAG_VIEW;
            case EntityType.folder: return UpdateStatusType.DYNAMIC_FOLDER_VIEW;
            case EntityType.searchResult: return UpdateStatusType.RESULT_VIEW;
            case EntityType.comparison: return UpdateStatusType.COMPARISON;
            case EntityType.savedSearch: return UpdateStatusType.SAVED_SEARCH;
            case EntityType.profileHistory: return UpdateStatusType.PROFILE;
            case EntityType.workFlow: return UpdateStatusType.WORK_FLOW;
            // add more as needed
        }
    }

    updateStatusTypeToEntityType(updateStatusType: UpdateStatusType): EntityType {
        switch (updateStatusType) {
            case UpdateStatusType.CORPUS: return EntityType.import;
            case UpdateStatusType.EXPORT_VIEW: return EntityType.export;
            case UpdateStatusType.WORKING_SET: return EntityType.projectData;
            case UpdateStatusType.CUSTODIAN: return EntityType.custodian;
            case UpdateStatusType.MEDIAID: return EntityType.mediaID;
            case UpdateStatusType.BATCH: return EntityType.batch;
            case UpdateStatusType.TAG_VIEW: return EntityType.tagView;
            case UpdateStatusType.DYNAMIC_FOLDER_VIEW: return EntityType.folder;
            case UpdateStatusType.RESULT_VIEW: return EntityType.searchResult;
            case UpdateStatusType.COMPARISON: return EntityType.comparison;
            case UpdateStatusType.SAVED_SEARCH: return EntityType.savedSearch;
            case UpdateStatusType.WORK_FLOW: return EntityType.workFlow;
            // add more as needed
        }
    }

    viewTypeToEntityType(viewType: ViewType): EntityType {
        switch (viewType) {
            case ViewType.CUSTODIAN:
            case ViewType.UNASSIGNED_CUSTODIAN:
                return EntityType.custodian;
            case ViewType.WORKING_SET:
                return EntityType.projectData;
            case ViewType.CORPUS_RESULT:
            case ViewType.CORPUS_SET_RESULT:
            case ViewType.WORKING_SET_RESULT:
                return EntityType.searchResult;
            case ViewType.TAG:
                return EntityType.tagView;
            case ViewType.EXPORT:
                return EntityType.export;
            case ViewType.DYNAMIC_FOLDER:
                return EntityType.folder;
            case ViewType.BATCH:
            case ViewType.UNASSIGNED_BATCH:
                return EntityType.batch;
            case ViewType.MEDIAID:
            case ViewType.UNASSIGNED_MEDIAID:
                return EntityType.mediaID;
            case ViewType.CORPUS:
            case ViewType.CORPUS_SET:
                return EntityType.import;
            case ViewType.REVIEW_BATCH_FOLDER:
                return EntityType.reviewFolder;
            case ViewType.REVIEW_BATCH:
                return EntityType.reviewBatch;
        }
    }

    downloadData(jsonData: any, filename: string, element: any): void {
        let csvRows = [];

        for (let i = 0, l = jsonData.length; i < l; ++i) {
            csvRows.push(jsonData[i].join(','));
        }

        const csvString = csvRows.join(this.currentBrowserIsIE ? "\r\n" : '%0A');
        if (window.navigator.msSaveOrOpenBlob) {
            window.navigator.msSaveOrOpenBlob(new Blob([csvString]), filename);
        } else {
            element.href = 'data:attachment/csv,' + csvString;
            element.target = '_blank';
            element.download = filename;
            setTimeout(function () {
                element.click();
            }, 1000);
        }
    }

    unzipFile(url: string, element: any, name: string, filter?: any, filterQuery?: any, useFileNameProvided?: boolean): void {
        let formatToUse, typeToUse = '';

        if (url.indexOf('/ediscovery') === -1) {
            url = '/ediscovery' + url;
        }

        if (this.currentBrowser === 'Safari' || url.indexOf('xlsx=true') !== -1 || name === 'ScanReport' || url.indexOf('allTemplates=true') !== -1) {
            url += '&unsupported=true';

            if (filter && filterQuery) {
                url += '&fhandle=' + filterQuery;
            }

            element.href = this.nonRestUrl + url;
            element.target = '_blank';
            element.click();
            return;
        }

        if (url.indexOf('format=ZIP') !== -1) {
            formatToUse = '.zip';

            if (filter && filterQuery) {
                url += '&fhandle=' + filterQuery;
            }

            const downloadZip = async () => {
                try {
                    const response = await fetch(this.nonRestUrl + url);
                    if (!response.ok) {
                        throw new Error(`HTTP error! status: ${response.status}`);
                    }
                    const blob = await response.blob();
                    const urlBlob = URL.createObjectURL(blob);
                    element.href = urlBlob;
                    element.download = useFileNameProvided ? name : name + formatToUse;
                    element.click();
                    setTimeout(() => {
                        URL.revokeObjectURL(urlBlob);
                    }, 1000);
                } catch (error) {
                    console.error('Error downloading ZIP file:', error.message);
                }
            };
            downloadZip();
            return;
        } else if (url.indexOf('format=XML') !== -1) {
            formatToUse = '.xml';
            typeToUse = 'text/' + formatToUse + ';charset=utf-8';
        } else {
            formatToUse = '.csv';
            typeToUse = 'text/' + formatToUse + ';charset=utf-8';
        }

        const archiveMaxSizeLimit = 200000000;
        let unzipDone = false;

        const downloadAndProcessZip = async (url: string, element: any, name: string, typeToUse: string, formatToUse: string, archiveMaxSizeLimit: number) => {
            try {
                unzipDone = true;
                // Create a ZipReader instance using HttpReader
                const zipReader = new zip.ZipReader(new zip.HttpReader(url));
                // Get all entries from the zip file
                const entries = await zipReader.getEntries();
                const self = this; // Store the current `this` context
                setTimeout(() => {
                    if (self?.downloadConfirmationModal && self?.downloadConfirmationModal?.baseModalRef) {
                        self.downloadConfirmationModal.baseModalRef.hide();
                    }
                }, 5000);
                if (entries.length > 0) {
                    const entry = entries[0];
                    if (entry.compressedSize > archiveMaxSizeLimit) {
                        await zipReader.close();
                        element.href = url;
                        element.download = useFileNameProvided ? entries[0].filename : name + formatToUse;
                        element.click();
                    } else {
                        // Get the data from the first entry
                        const data: any = await entry.getData(new zip.BlobWriter(typeToUse));

                        // Close the reader
                        await zipReader.close();

                        // Handle the data
                        const blob = new Blob([data], { type: typeToUse });
                        const urlBlob = URL.createObjectURL(blob);
                        element.href = urlBlob;
                        element.download = useFileNameProvided ? entries[0].filename : name + formatToUse;
                        setTimeout(() => {
                            element.click();
                        }, 1000);
                    }
                }
            } catch (error) {
                console.error('Error processing zip file:', error);
                if (error.message.includes('File is using Zip64 (4gb+ file size).')) {
                    console.log('Download And Unzip info: ' + error);
                } else {
                    alert(error.message);
                    console.log('Download error: ' + error);
                }
            }
        }

        downloadAndProcessZip(url, element, name, typeToUse, formatToUse, archiveMaxSizeLimit);

        setTimeout(() => {
            if (!unzipDone) {
                this.openInformationToastNotification('Report download is being generated. Download will start automatically.');
                unzipDone = false;
            }
        }, 5000);
    }

    insertYesterday(dateStr: string): string {
        const dayInMiliseconds = 8.64e7;
        const now = new Date();
        now.setHours(0, 0, 0, 0);
        if (dateStr) {
            const originalDaysSinceEpoch = Math.floor(Date.parse(dateStr.split(' ')[0]) / dayInMiliseconds),
                currentMonth = now.getMonth() + 1,
                currentDate = now.getDate(),
                todaysDaysSinceEpoch = Math.floor(Date.parse(now.getFullYear() + '-' + (currentMonth < 10 ? '0' + currentMonth : currentMonth) + '-' + (currentDate < 10 ? '0' + currentDate : currentDate)) / dayInMiliseconds);
            if (originalDaysSinceEpoch === todaysDaysSinceEpoch) {
                dateStr = 'Today ' + dateStr.split(' ')[1];
            } else if (originalDaysSinceEpoch + 1 === todaysDaysSinceEpoch) {
                dateStr = 'Yesterday ' + dateStr.split(' ')[1];
            }
        }
        return dateStr;
    }

}
